import { useMemo, useRef, useState } from 'react';
import {
  TextField,
  Autocomplete,
  FormControl,
  FormHelperText,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { Option } from '@/components/inputs/AutocompleteInput';

const getOptionIdDefault = (option: Option): string =>
  typeof option.data === 'string'
    ? option.data
    : (option.data as { id?: string } | null)?.id ?? '';

export interface AutocompleteMultiInputProps {
  name: string;
  placeholder: string;
  value: string[];
  onChange: (value: string[]) => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onBlur?: () => void;
  options: Option[];
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  errorInfo?: string;
  autoFocus?: boolean;
  getOptionId?: (option: Option) => string;
  /** Etiquetas por id para los seleccionados que no estan en options. */
  labelsById?: Record<string, string>;
  /** Mantiene el orden de options y agrega al final los seleccionados que no estan. */
  keepOptionsOrder?: boolean;
  /** Muestra la seleccion en una sola linea con "+N" en vez de todos los nombres. */
  compactSelection?: boolean;
  /** No borra el texto de busqueda al marcar o desmarcar una opcion. */
  keepInputOnSelect?: boolean;
}

const COMPACT_VISIBLE_LABELS = 2;
const SELECTION_INPUT_REASONS = new Set(['selectOption', 'removeOption']);

const AutocompleteMultiInput = ({
  name,
  placeholder,
  errorInfo,
  required = false,
  error = false,
  disabled = false,
  options,
  value,
  onChange,
  inputValue: inputValueProp,
  onInputChange: onInputChangeProp,
  onBlur,
  loading = false,
  autoFocus = false,
  getOptionId = getOptionIdDefault,
  labelsById,
  keepOptionsOrder = false,
  compactSelection = false,
  keepInputOnSelect = false,
}: AutocompleteMultiInputProps) => {
  const [internalSearch, setInternalSearch] = useState('');

  const isControlled = onInputChangeProp !== undefined;
  const inputValue = isControlled ? (inputValueProp ?? '') : internalSearch;
  const setInputValue = isControlled ? onInputChangeProp! : setInternalSearch;

  // Las opciones llegan de una busqueda: las ya elegidas pueden no estar en la
  // lista actual. Se recuerdan para no perder la seleccion al seguir tipeando.
  const knownOptionsByIdRef = useRef(new Map<string, Option>());
  for (const option of options ?? []) {
    const optionId = getOptionId(option);
    if (optionId) knownOptionsByIdRef.current.set(optionId, option);
  }

  const selectedOptions = useMemo(() => {
    if (!value?.length) return [];
    return value.map(
      (id) =>
        knownOptionsByIdRef.current.get(id) ?? {
          label: labelsById?.[id] ?? id,
          data: id,
        }
    );
  }, [value, options, labelsById, getOptionId]);

  const mergedOptions = useMemo(() => {
    const byId = new Map<string, Option>();
    const addAll = (list: Option[]) => {
      for (const option of list) {
        const optionId = getOptionId(option);
        if (optionId && (!keepOptionsOrder || !byId.has(optionId))) {
          byId.set(optionId, option);
        }
      }
    };
    if (keepOptionsOrder) {
      addAll(options ?? []);
      addAll(selectedOptions);
    } else {
      addAll(selectedOptions);
      addAll(options ?? []);
    }
    return [...byId.values()];
  }, [selectedOptions, options, getOptionId, keepOptionsOrder]);

  return (
    <FormControl error={error} required={required} disabled={disabled} fullWidth>
      <Autocomplete
        id={name}
        multiple
        value={selectedOptions}
        inputValue={inputValue}
        onInputChange={(_, newValue, reason) => {
          if (isControlled && reason === 'reset') return;
          if (keepInputOnSelect && SELECTION_INPUT_REASONS.has(reason)) return;
          setInputValue(newValue);
        }}
        onChange={(_, newValue) =>
          onChange(newValue.map((opt) => getOptionId(opt)))
        }
        options={mergedOptions}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) =>
          getOptionId(option) === getOptionId(val)
        }
        disabled={disabled}
        loading={loading}
        loadingText="Cargando…"
        disableCloseOnSelect
        renderInput={(params) => (
          <TextField
            {...params}
            label={placeholder}
            variant="outlined"
            required={required}
            disabled={disabled}
            error={error}
            autoFocus={autoFocus}
            fullWidth
            onBlur={onBlur}
          />
        )}
        renderTags={(tagValue) => {
          if (tagValue.length === 0) return [];
          if (!compactSelection) {
            return [
              <span key="selected">
                {tagValue.map((o) => o.label).join(', ')}
              </span>,
            ];
          }
          const visible = tagValue
            .slice(0, COMPACT_VISIBLE_LABELS)
            .map((o) => o.label)
            .join(', ');
          const hidden = tagValue.length - COMPACT_VISIBLE_LABELS;
          return [
            <span
              key="selected"
              title={tagValue.map((o) => o.label).join(', ')}
              style={{
                maxWidth: '70%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {hidden > 0 ? `${visible} +${hidden}` : visible}
            </span>,
          ];
        }}
        getOptionKey={getOptionId}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <li {...optionProps} key={key}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              <ListItemText primary={option.label} />
            </li>
          );
        }}
        fullWidth
        noOptionsText="No se encontraron opciones"
        clearOnEscape
      />
      <FormHelperText>{errorInfo}</FormHelperText>
    </FormControl>
  );
};

export default AutocompleteMultiInput;
